import React, { useEffect, useState } from 'react';
import { Button, FormHelperText, Group, Input, Typography, Wrapper } from '@screentone/core';

import { ConfirmationDialog } from 'components';
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { usePageLock } from 'contexts/page-lock/usePageLock';
import {
  AvailableFeatureFlags,
  CollectionDtoMetadata,
  useCollectionDtoClearExternalIdMutation,
  useCollectionDtoSetExternalIdMutation
} from 'data/generated/graphql';
import { useIsAdmin, useIsSuper } from 'hooks/roles';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import { reportError } from 'utils/errorBoundary';
import styles from './CollectionExternalId.module.scss';

export const CollectionExternalId = () => {
  const isAdmin = useIsAdmin();
  const isSuper = useIsSuper();
  const { state } = usePageLock();
  const { isReadOnly } = state.publishing;
  const { metadata, setNewMetadata } = useDataModelContext();
  const featureEnabled = useFeatureFlag(AvailableFeatureFlags.EnableCollectionExternalId, false);

  const persistedExternalId = (metadata as CollectionDtoMetadata).externalId ?? '';
  const canEdit = isAdmin || isSuper;

  const [draft, setDraft] = useState(persistedExternalId);
  const [isEditing, setIsEditing] = useState(!persistedExternalId);
  const [showChangeConfirm, setShowChangeConfirm] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  useEffect(() => {
    if (isReadOnly) {
      setDraft(persistedExternalId);
      setIsEditing(false);
      setShowChangeConfirm(false);
      setShowRemoveConfirm(false);
    }
  }, [isReadOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  const { mutateAsync: setExternalIdMutateAsync, isLoading: isSaving } = useCollectionDtoSetExternalIdMutation();
  const { mutateAsync: clearExternalIdMutateAsync, isLoading: isClearing } = useCollectionDtoClearExternalIdMutation();

  if (!featureEnabled || !metadata.allessehId) return null;

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setDraft(e.target.value.replace(/\s/g, ''));
  };

  const handleCancelEdit = () => {
    setDraft(persistedExternalId);
    setIsEditing(false);
  };

  const commitSave = async () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === persistedExternalId) return;

    try {
      await setExternalIdMutateAsync({
        collectionDTOSetExternalIdInput: {
          publicationKey: metadata.publicationKey,
          allessehId: metadata.allessehId,
          externalId: trimmed
        }
      });
      setNewMetadata({ externalId: trimmed });
      setIsEditing(false);
      setShowChangeConfirm(false);
    } catch (error) {
      reportError(`Failed to save External ID: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const handleSave = () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === persistedExternalId) return;
    if (persistedExternalId) {
      setShowChangeConfirm(true);
    } else {
      void commitSave();
    }
  };

  const commitRemove = async () => {
    try {
      await clearExternalIdMutateAsync({
        collectionDTOClearExternalIdInput: {
          publicationKey: metadata.publicationKey,
          allessehId: metadata.allessehId
        }
      });
      setNewMetadata({ externalId: undefined });
      setDraft('');
      setIsEditing(true);
      setShowRemoveConfirm(false);
    } catch (error) {
      reportError(`Failed to remove External ID: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  return (
    <>
      <Wrapper data-testid="collection-info-box-section-conf-external-id" margin={{ top: 'md' }}>
        <Typography
          data-testid="collection-info-box-section-conf-external-id-label"
          size="sm"
          weight="bold"
          margin={{ bottom: 'sm' }}
        >
          External ID
        </Typography>
        {persistedExternalId && !isEditing ? (
          <Group gap="xs" align="stretch">
            <Group.Item flex fullWidth>
              <Typography
                data-testid="collection-info-box-section-conf-external-id-text"
                className={styles.externalIdText}
                title={persistedExternalId}
              >
                {persistedExternalId}
              </Typography>
            </Group.Item>
            {canEdit && !isReadOnly && (
              <Group.Item>
                <Button
                  tertiary
                  data-testid="collection-info-box-section-conf-external-id-edit"
                  onClick={() => setIsEditing(true)}
                >
                  Edit
                </Button>
              </Group.Item>
            )}
          </Group>
        ) : (
          <>
            <FormHelperText>
              External Allesseh ID for dual-publish.
              {persistedExternalId &&
                ' Changing this leaves the previous external collection orphaned in Allesseh — reach out to the curation team to delete it if needed.'}
            </FormHelperText>
            <Group gap="xs" align="stretch">
              <Group.Item flex fullWidth>
                <Input
                  data-testid="collection-info-box-section-conf-external-id-input"
                  type="text"
                  placeholder="External collection ID"
                  value={draft}
                  onChange={handleChange}
                  disabled={!canEdit || isReadOnly}
                  readOnly={!canEdit || isReadOnly}
                />
              </Group.Item>
              {canEdit && !persistedExternalId && !isReadOnly && (
                <Group.Item>
                  <Button
                    tertiary
                    data-testid="collection-info-box-section-conf-external-id-save"
                    disabled={!draft.trim() || isSaving}
                    onClick={handleSave}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </Button>
                </Group.Item>
              )}
            </Group>
            {canEdit && persistedExternalId && !isReadOnly && (
              <Group margin={{ top: 'md' }}>
                <Group.Item>
                  <Button
                    tertiary
                    color="lava"
                    data-testid="collection-info-box-section-conf-external-id-remove"
                    disabled={isSaving || isClearing}
                    onClick={() => setShowRemoveConfirm(true)}
                  >
                    Remove
                  </Button>
                </Group.Item>
                <Group.Item>
                  <Button
                    tertiary
                    color="asphalt"
                    data-testid="collection-info-box-section-conf-external-id-cancel"
                    disabled={isSaving || isClearing}
                    onClick={handleCancelEdit}
                  >
                    Cancel
                  </Button>
                </Group.Item>
                <Group.Item>
                  <Button
                    tertiary
                    data-testid="collection-info-box-section-conf-external-id-save"
                    disabled={!draft.trim() || draft.trim() === persistedExternalId || isSaving || isClearing}
                    onClick={handleSave}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </Button>
                </Group.Item>
              </Group>
            )}
          </>
        )}
      </Wrapper>
      {showChangeConfirm && (
        <ConfirmationDialog
          title="Change External ID"
          description={
            <>
              You are about to change the External ID from <strong>{persistedExternalId}</strong> to{' '}
              <strong>{draft.trim()}</strong>. The previous external collection will become orphaned in Allesseh. Reach
              out to the curation team to delete it if needed.
            </>
          }
          showDialog={setShowChangeConfirm}
          onClickFunc={() => void commitSave()}
          isLoading={isSaving}
        />
      )}
      {showRemoveConfirm && (
        <ConfirmationDialog
          title="Remove External ID"
          description={
            <>
              Remove the External ID <strong>{persistedExternalId}</strong> from this collection? The collection will
              stop dual-publishing. The existing external collection in Allesseh will become orphaned — reach out to the
              curation team to delete it if needed.
            </>
          }
          showDialog={setShowRemoveConfirm}
          onClickFunc={() => void commitRemove()}
          isLoading={isClearing}
        />
      )}
    </>
  );
};
